/* eslint-disable no-unused-vars */
import {
  AuthorizationCodeAlreadyUsedError,
  AuthorizationCodeExpiredError,
  AuthorizationCodeMissingError,
  BadRequestError,
  ClerkError,
  ConflictError,
  ExpectedError,
  ExternalError,
  ForbiddenError,
  IntegrationInstallationNotFoundError,
  IntegrationNotFoundError,
  InternalError,
  LocationNotFoundError,
  MismatchedOrgIdError,
  NoFacebookPagesFoundError,
  NoOrgIdError,
  NotFoundError,
  NotificationError,
  OAuthError,
  PrismaError,
  RaisedError,
  UnauthorizedError,
  WebsiteNotFoundError,
} from "../errors";
import { ERROR_TYPES } from "./errorTypes";

// Map of error types to their corresponding error classes
const ERROR_CLASS_MAP = {
  [ERROR_TYPES.WEBSITE_NOT_FOUND_ERROR]: WebsiteNotFoundError,
  [ERROR_TYPES.INTEGRATION_NOT_FOUND_ERROR]: IntegrationNotFoundError,
  [ERROR_TYPES.LOCATION_NOT_FOUND_ERROR]: LocationNotFoundError,
  [ERROR_TYPES.INTEGRATION_INSTALLATION_NOT_FOUND_ERROR]: IntegrationInstallationNotFoundError,
  [ERROR_TYPES.NOT_FOUND_ERROR]: NotFoundError,
  [ERROR_TYPES.UNAUTHORIZED_ERROR]: UnauthorizedError,
  [ERROR_TYPES.NO_ORG_ID_ERROR]: NoOrgIdError,
  [ERROR_TYPES.CONFLICT_ERROR]: ConflictError,
  [ERROR_TYPES.FORBIDDEN_ERROR]: ForbiddenError,
  [ERROR_TYPES.MISMATCHED_ORG_ID_ERROR]: MismatchedOrgIdError,
  [ERROR_TYPES.OAUTH_ERROR]: OAuthError,
  [ERROR_TYPES.AUTHORIZATION_CODE_ALREADY_USED_ERROR]: AuthorizationCodeAlreadyUsedError,
  [ERROR_TYPES.AUTHORIZATION_CODE_EXPIRED_ERROR]: AuthorizationCodeExpiredError,
  [ERROR_TYPES.AUTHORIZATION_CODE_MISSING_ERROR]: AuthorizationCodeMissingError,
  [ERROR_TYPES.BAD_REQUEST_ERROR]: BadRequestError,
  [ERROR_TYPES.NO_FACEBOOK_PAGES_FOUND_ERROR]: NoFacebookPagesFoundError,
  [ERROR_TYPES.INTERNAL_ERROR]: InternalError,
  [ERROR_TYPES.EXPECTED_ERROR]: ExpectedError,
  [ERROR_TYPES.EXTERNAL_ERROR]: ExternalError,
  [ERROR_TYPES.NOTIFICATION_ERROR]: NotificationError,
  [ERROR_TYPES.PRISMA_ERROR]: PrismaError,
  [ERROR_TYPES.CLERK_ERROR]: ClerkError,
} as const;

/**
 * Checks if an error is an instance of any of our custom error classes
 * @param error - The error to check
 * @returns boolean indicating if the error is a custom error
 */
// make this a type predicate
export function isCustomError(error: unknown): error is Error {
  if (!(error instanceof Error)) return false;

  return Object.values(ERROR_CLASS_MAP).some((ErrorClass) => error instanceof ErrorClass);
}

/**
 * Gets the specific error type for a custom error
 * @param error - The error to check
 * @returns The ERROR_TYPES value if it's a custom error, undefined otherwise
 */
export function getCustomErrorType(error: unknown): keyof typeof ERROR_TYPES | undefined {
  if (!(error instanceof Error)) return undefined;

  const entry = Object.entries(ERROR_CLASS_MAP).find(([_, ErrorClass]) => error instanceof ErrorClass);
  return entry ? (entry[0] as keyof typeof ERROR_TYPES) : undefined;
}

type Success<T> = {
  data: T;
  error: null;
};

type Failure<E> = {
  data: null;
  error: E;
};

type Result<T, E = Error> = Success<T> | Failure<E>;

/**
 * returns true when an error represents a server-side / unexpected failure
 * (5xx or unknown) rather than expected client-side business logic (4xx).
 * use alongside tryCatch to decide whether an error warrants sentry capture.
 */
export function isServerError(error: unknown): boolean {
  return !(error instanceof RaisedError) || error.statusCode >= 500;
}

// overloaded wrapper function for both promises and async functions as well as regular functions
export async function tryCatch<T, E = Error>(promise: Promise<T>): Promise<Result<T, E>>;
export async function tryCatch<T, E = Error>(asyncFn: () => Promise<T>): Promise<Result<T, E>>;
export async function tryCatch<T, E = Error>(fn: () => T): Promise<Result<T, E>>;
export async function tryCatch<T, E = Error>(promiseOrFn: Promise<T> | (() => Promise<T>)): Promise<Result<T, E>> {
  try {
    const data = typeof promiseOrFn === "function" ? await promiseOrFn() : await promiseOrFn;
    return { data, error: null };
  } catch (error) {
    return { data: null, error: error as E };
  }
}
